import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import { JoyAi } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAutoPopulation, useHasSeenOnboardingVenueDialog } from '@shared/utils/accommodations';
import { useTour } from '@shared/components/Tour';
import { useIsAccommodationsTourEnabled } from '@shared/utils/accommodations/useIsAccommodationsTourEnabled';
import { RoomBlockRatesDialog } from './RoomBlockRatesDialog';
import { useUserConsent } from '@shared/utils/userConsent';
import { useWebsiteEditorDialogContext } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_DIALOGS } from '../../Accommodations.dialogs';
import { DialogReadiness } from '@shared/hooks/useDialogManager';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useEventVenueDialog } from '../EventVenueDialog/useEventVenueDialog';
import { useWebsiteEditorContext } from '@apps/admin/routes/WebsiteEditor/context/WebsiteEditorContext/WebsiteEditor.context';
import { AutoPopulatedMoreHotelsTooltip } from './AutoPopulatedMoreHotelsTooltip';
import { useAutoPopulatedAccommodations } from '../../hooks/useAutoPopulatedAccommodations';

/**
 * Button component that opens the room block rates dialog.
 * Only renders if there are newly populated accommodations and the feature flag is enabled.
 * Displays an AI icon button that opens the dialog when clicked.
 */
export const StartTourButton = ({ showTour }: { showTour: boolean }) => {
  const { tq } = useTranslation('travelMap');

  const { registerDialog, dismiss, isOpen: isDialogOpen, requestShow } = useWebsiteEditorDialogContext(ACCOMMODATIONS_DIALOGS.ACCOMMODATION_TOUR_TOOLTIP);
  const tourDialogIsOpen = isDialogOpen();

  // Persist the tour dialog open state until the tour is completed, so that dialog manager doesn't consider it as closed when the dialog is dismissed.
  const [isRoomBlockDialogOpen, setIsRoomBlockDialogOpen] = useState(tourDialogIsOpen);

  const tour = useTour();
  const history = useHistory();
  const routerHelper = useRouterHelper();
  const { venueInfo } = useEventPlaces();
  const { isAccommodationsTourEnabled } = useIsAccommodationsTourEnabled();

  const { loadingData, autoPopulatedAccommodations, isFirstTimeAutoPopulation } = useAutoPopulatedAccommodations();
  const newlyPopulatedCount = autoPopulatedAccommodations.length;

  const { requestOpen, isDashboardRedirect } = useEventVenueDialog();
  const { setHasSeenOnboardingVenueDialog } = useHasSeenOnboardingVenueDialog();

  const { isVisiblePreview } = useWebsiteEditorContext();

  const { status } = useAutoPopulation({ mode: 'poll-only', context: 'StartTourButton' });

  const { getUserConsent, setUserConsent, loadingUserConsent } = useUserConsent();

  // Determine if the tour dialog should be shown
  const dialogReadiness = useMemo((): DialogReadiness => {
    if (loadingUserConsent || loadingData || status === 'PENDING') return 'loading';

    const venueDialogTakesPriority = isDashboardRedirect;
    if (venueDialogTakesPriority) return 'skip';

    const hasSeenAccommodationsTour = getUserConsent('hasSeenAccommodationsTour');

    // Check all conditions:
    // 1. Auto population is completed (and not loading)
    // 2. Feature flag is enabled
    // 3. User hasn't seen the accommodations tour yet
    if (status === 'COMPLETED' && isAccommodationsTourEnabled && !hasSeenAccommodationsTour) {
      return 'ready';
    }

    return 'skip';
  }, [loadingUserConsent, loadingData, getUserConsent, status, isAccommodationsTourEnabled, isDashboardRedirect]);

  const prevDialogReadinessRef = useRef<DialogReadiness | null>(null);

  useEffect(() => {
    registerDialog(dialogReadiness);

    if (prevDialogReadinessRef.current === 'skip' && dialogReadiness === 'ready') {
      requestShow();
    }

    prevDialogReadinessRef.current = dialogReadiness;
  }, [registerDialog, dialogReadiness, requestShow]);

  // Dialog manager rejects after dismissal, but wants to re-request when status newly hits COMPLETED
  const shouldRequestShow = useMemo(() => {
    return (
      // All conditions must be met for the dialog to be shown
      dialogReadiness === 'ready' &&
      // Tour dialog is not currently open
      !tourDialogIsOpen &&
      // At least one accommodation was auto-populated
      newlyPopulatedCount > 0 &&
      // User hasn't seen the tour yet
      !getUserConsent('hasSeenAccommodationsTour') &&
      // On mobile we toggle the preview  vs edit mode, so we don't want to show the tour dialog in preview mode.
      !isVisiblePreview &&
      // Show the tour button if the showTour flag is true
      showTour
    );
  }, [dialogReadiness, tourDialogIsOpen, newlyPopulatedCount, getUserConsent, isVisiblePreview, showTour]);

  useEffect(() => {
    if (shouldRequestShow) {
      requestShow();
    }
  }, [shouldRequestShow, requestShow]);

  // Open the dialog when the button is clicked
  const handleButtonClick = useEventCallback(() => {
    requestShow();
  });

  const markTourAsSeen = () => {
    if (!getUserConsent('hasSeenAccommodationsTour')) {
      setUserConsent('hasSeenAccommodationsTour', true);
    }
  };

  const handleShowHotelPicks = useEventCallback(() => {
    setIsRoomBlockDialogOpen(false);
    requestAnimationFrame(() => {
      // If have auto populated hotels, run the tour and mark tour as seen
      if (newlyPopulatedCount > 0) {
        tour.state.stateProps.run();
        // Once the tour is run, we can mark it as seen.
        markTourAsSeen();
      } else if (!venueInfo.hasVenue) {
        // If no venue, dismiss the tour dialog, show add venue dialog, mark venue dialog as seen
        dismiss();
        requestOpen();
        setHasSeenOnboardingVenueDialog();
        // Once the venue dialog is shown, we will auto populate the hotels. and tour will be run again, from venue dialog.
        // We need to mark tour as seen, so that the tour dialog won't be shown again.
        markTourAsSeen();
      } else {
        // If have venue and don't have auto populated hotels, dismiss the tour dialog, redirect to browse joy hotels page, mark tour as seen
        dismiss();
        // We have venue but don't have auto populated hotels, we need to mark as seen. b/c we are not going to auto populate again.
        markTourAsSeen();
        history.push(routerHelper.buildPath(`${PlanningRoutes.accommodations}/browse`));
      }
    });
  });

  const handleDialogClose = useEventCallback(() => {
    dismiss();
    setIsRoomBlockDialogOpen(false);
    markTourAsSeen();
  });

  // Open the dialog when the tour dialog is opened
  useEffect(() => {
    if (tourDialogIsOpen) {
      setIsRoomBlockDialogOpen(true);
    }
  }, [tourDialogIsOpen]);

  // Dismiss dialog when tour closes (handles both completion and manual close)
  const prevTourOpenRef = useRef(tour.state.stateProps.isOpen);
  useEffect(() => {
    // If tour was open and is now closed, dismiss the dialog
    if (prevTourOpenRef.current && !tour.state.stateProps.isOpen) {
      dismiss();
    }
    prevTourOpenRef.current = tour.state.stateProps.isOpen;
  }, [tour.state.stateProps.isOpen, dismiss]);

  if (!isAccommodationsTourEnabled || status === 'PENDING') {
    return null;
  }

  const hasSeenTour = Boolean(getUserConsent('hasSeenAccommodationsTour'));

  // Tooltip: returning auto-population only, if the tour has not been seen
  const shouldShowAutoPopulatedTooltip = isRoomBlockDialogOpen && !isFirstTimeAutoPopulation && !hasSeenTour;

  // Dialog: first auto-population or user has already seen the tour
  const shouldShowRoomBlockDialog = isRoomBlockDialogOpen && (isFirstTimeAutoPopulation || hasSeenTour);

  return (
    <>
      {/* Show the button only if there are newly populated hotels and the venue is present */}
      {venueInfo.hasVenue && newlyPopulatedCount > 0 && (
        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={handleButtonClick}
          aria-label={tq`Start accommodations tour`}
          paddingX={0}
          minWidth={pxToRem(32)}
          width={pxToRem(32)}
          height={pxToRem(32)}
        >
          <JoyAi
            size={32}
            color="newUIBrandPrimary"
          />
        </ButtonV2>
      )}
      {/* Tour Dialog should be visible on first time, even if don't have venue and auto populated hotels */}
      <RoomBlockRatesDialog
        isOpen={shouldShowRoomBlockDialog}
        onCloseDialog={handleDialogClose}
        onShowHotelPicks={handleShowHotelPicks}
        hotelCount={newlyPopulatedCount}
        hasVenue={venueInfo.hasVenue}
      />
      {/* Tooltip for auto populated more hotels from save venue */}
      <AutoPopulatedMoreHotelsTooltip
        isOpen={shouldShowAutoPopulatedTooltip}
        onCloseDialog={handleDialogClose}
        onShowHotelPicks={handleShowHotelPicks}
      />
    </>
  );
};
